import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { colorForPerson } from "../lib/personColor";

export interface AssigneeOption {
  id: string;
  name: string;
}

export interface AssigneePickerSheetProps {
  open: boolean;
  /** The piece's title — rendered into the sheet's dynamic dialog title,
   * matching the mock exactly: `Who's doing "<piece>"?`. Ignored when
   * `title` is given (see below). */
  pieceTitle: string;
  /** Sprint-010 C4 (design-phase-c.md §5.4) — Quick Add's animal-picker
   * step reuses this sheet's chrome with a literal title ("Which
   * animal?"), not the "Who's doing …" template built for a chore/task
   * piece. When given, this REPLACES the templated title entirely. */
  title?: string;
  options: AssigneeOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCancel: () => void;
  /** Sprint-010 C4 (design-phase-c.md §5.4) — Quick Add's animal picker
   * explicitly drops the per-person color dot ("no color dots — animals
   * aren't color-coded anywhere in this app"). Defaults to `true` so
   * every existing caller (person pickers) is unchanged. */
  showAvatar?: boolean;
  /** Sprint-017 E7 (design §4.2) — an optional row rendered ABOVE the person
   * list (the project-task assign flow slots its "Safety task" `role="switch"`
   * row here). A prop-level slot, NOT a fork — omitted by every existing
   * caller, whose sheet is byte-identical to before. */
  topSlot?: ComponentChildren;
  /** Sprint-017 E7 (design §4.2) — an optional "clear the current choice"
   * row rendered below the list (the assign flow's "Nobody yet", which
   * clears the assignee back to "Up for grabs"). When given, tapping it
   * fires `onClear` and the sheet's caller closes. */
  onClear?: () => void;
  clearLabel?: string;
}

/**
 * The bottom-sheet person picker (design-spec-today-card.md §2's
 * Assignee-picker sheet molecule, rev-2 fix for B3): every helper listed
 * simultaneously as a big (>=48px), fully-labeled row — no cycling, no
 * hidden options. One tap assigns and closes; Cancel, the backdrop, and
 * Escape all dismiss without changing anything.
 */
export function AssigneePickerSheet({
  open,
  pieceTitle,
  title,
  options,
  selectedId,
  onSelect,
  onCancel,
  showAvatar = true,
  topSlot,
  onClear,
  clearLabel,
}: AssigneePickerSheetProps) {
  const titleId = "assignee-picker-title";
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sprint-011 C1 (TD-012, SA plan §3 clause 3) — the backdrop and Cancel
  // button route through the hook's returned `close`, not `onCancel`
  // directly, so a hardware/browser Back converges on the same path.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: firstOptionRef,
  });

  if (!open) return null;

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id={titleId}>
          {title ?? <>Who's doing &quot;{pieceTitle}&quot;?</>}
        </h2>
        {topSlot}
        <ul class="assign-sheet-list">
          {options.map((option, index) => {
            const isSelected = option.id === selectedId;
            return (
              <li key={option.id}>
                <button
                  ref={index === 0 ? firstOptionRef : undefined}
                  type="button"
                  class={`assign-option${isSelected ? " is-selected" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(option.id)}
                >
                  <span class="assign-option-main">
                    {showAvatar ? (
                      <span
                        class="name-dot"
                        aria-hidden="true"
                        style={{ background: colorForPerson(option.id) }}
                      >
                        {option.name.charAt(0)}
                      </span>
                    ) : null}
                    <span>{option.name}</span>
                  </span>
                  <span class="check-mark" aria-hidden="true">
                    ✓
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {onClear ? (
          <button type="button" class="secondary-btn" onClick={onClear}>
            {clearLabel ?? "Nobody yet"}
          </button>
        ) : null}
        <button type="button" class="secondary-btn" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
